import type { ReactiveController, ReactiveControllerHost } from 'lit'
import { isServer } from 'lit'

import { prefersReducedMotion } from '../prefers-reduced-motion.js'
import { StateAttributesController } from './StateAttributesController.js'
import { TransitionStatusController } from './TransitionStatusController.js'

export interface CollapseOptions {
  /** Reads the host's open state; diffed after every host update. */
  open: () => boolean
  /**
   * The CSS custom property set to the measured content height during the
   * animation (e.g. `--n-accordion-panel-height`), for consumers to read.
   */
  heightVar: string
  /** The element to animate. Defaults to the host. */
  target?: () => HTMLElement
}

/**
 * Animates a host's `block-size` between `0` and its measured content height as
 * its `open` state toggles — the shared engine behind the accordion and
 * collapsible panels. It measures the live rendered height (so rapid re-toggles
 * continue smoothly rather than snapping), respects reduced motion, and cleans
 * up on disconnect.
 *
 * It owns a {@link TransitionStatusController} (+ a {@link StateAttributesController})
 * so the host also gains `data-open` / `data-closed` / `data-starting-style` /
 * `data-ending-style` state hooks, driven off the same `block-size` transition.
 */
export class CollapseController implements ReactiveController {
  private _animating = false
  private previousOpen?: boolean

  /** Exposes the transition status (and reflects the state attributes). */
  private transitionStatus: TransitionStatusController
  private stateAttributes: StateAttributesController

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options: CollapseOptions,
  ) {
    this.transitionStatus = new TransitionStatusController(host, {
      open: options.open,
      target: options.target,
    })
    this.stateAttributes = new StateAttributesController(host, () => this.transitionStatus.state)
    host.addController(this)
  }

  /** Whether a `block-size` transition is currently running. */
  get animating(): boolean {
    return this._animating
  }

  get status() {
    return this.transitionStatus.status
  }

  private get target(): HTMLElement {
    return this.options.target?.() ?? this.host
  }

  hostConnected() {
    const el = this.target
    el.addEventListener('transitionend', this.handleTransitionEnd)
    el.addEventListener('transitioncancel', this.handleTransitionCancel)
  }

  hostDisconnected() {
    const el = this.target
    el.removeEventListener('transitionend', this.handleTransitionEnd)
    el.removeEventListener('transitioncancel', this.handleTransitionCancel)
    // Clear any in-flight inline styles so a reconnect starts from a clean slate.
    el.style.removeProperty('block-size')
    el.style.removeProperty(this.options.heightVar)
    this._animating = false
  }

  hostUpdated() {
    const open = this.options.open()
    if (open === this.previousOpen) {
      return
    }
    const first = this.previousOpen === undefined
    this.previousOpen = open

    // The initial state is applied by the host (e.g. `hidden = !open`) without
    // animation; only subsequent toggles animate.
    if (first) {
      return
    }

    if (open) {
      this.animateOpen()
    }
    else {
      this.animateClose()
    }
  }

  /**
   * Measure the height to animate from. While a transition is in flight the box
   * is mid-collapse/expand, so `scrollHeight` is clipped — use the live rendered
   * height instead so we never snap to a stale full/zero height.
   */
  private currentHeight(): number {
    const el = this.target
    return this._animating ? el.getBoundingClientRect().height : el.scrollHeight
  }

  private animateOpen() {
    const el = this.target
    el.hidden = false

    if (prefersReducedMotion() || isServer) {
      this._animating = false
      el.style.removeProperty(this.options.heightVar)
      el.style.removeProperty('block-size')
      return
    }

    // Start from the collapsed height (0) for a fresh open; only when a previous
    // transition is still running do we pin the partial rendered height so the
    // open continues smoothly. (`el.hidden` was just cleared above, so reading
    // scrollHeight/getBoundingClientRect here would report the full natural
    // height and animate from full→full — i.e. not animate at all.)
    const start = this._animating ? el.getBoundingClientRect().height : 0
    el.style.setProperty('block-size', `${start}px`)
    void el.offsetHeight

    // Measure the natural target height with the fixed block-size removed, then
    // restore the start so the transition has a defined point to animate from.
    el.style.removeProperty('block-size')
    const target = el.scrollHeight
    el.style.setProperty('block-size', `${start}px`)
    void el.offsetHeight

    el.style.setProperty(this.options.heightVar, `${target}px`)
    this._animating = true
    el.style.setProperty('block-size', `${target}px`)
  }

  private animateClose() {
    const el = this.target

    if (prefersReducedMotion() || isServer) {
      this._animating = false
      el.style.removeProperty(this.options.heightVar)
      el.style.removeProperty('block-size')
      el.hidden = true
      return
    }

    const height = this.currentHeight()
    el.style.setProperty(this.options.heightVar, `${height}px`)
    el.style.setProperty('block-size', `${height}px`)
    void el.offsetHeight

    this._animating = true
    el.style.setProperty('block-size', '0px')
  }

  private handleTransitionEnd = (event: TransitionEvent) => {
    // Only react to our own block-size transition, not bubbled child ones.
    if (event.target !== this.target || event.propertyName !== 'block-size') {
      return
    }

    this._animating = false
    const el = this.target
    el.style.removeProperty('block-size')
    el.style.removeProperty(this.options.heightVar)

    if (!this.options.open()) {
      el.hidden = true
    }
  }

  // On an interrupted transition (rapid re-toggle) the replacement animation has
  // already been scheduled by the new open/close, so just clear the flag — do
  // NOT reset styles, which would clobber the new transition's start height.
  private handleTransitionCancel = (event: TransitionEvent) => {
    if (event.target !== this.target || event.propertyName !== 'block-size') {
      return
    }
    this._animating = false
  }
}
